<script setup lang="ts">
import { computed } from 'vue'
import { formatDistanceToNow } from 'date-fns'
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-vue-next'
import { Badge } from '@/components/ui'
import { useEntityTaskStats } from '@/composables/useEntityTaskStats'

const props = defineProps<{
  entityType: string
}>()

const stats = useEntityTaskStats(props.entityType)

const variant = computed(() => {
  if (stats.value.hasFailures) return 'error'
  if (stats.value.count > 0) return 'warning'
  return 'muted'
})

const icon = computed(() => {
  if (stats.value.hasFailures) return AlertTriangle
  if (stats.value.count > 0) return Loader2
  return CheckCircle2
})

const label = computed(() => {
  if (stats.value.hasFailures) {
    if (stats.value.count > 0) {
      const ago = stats.value.lastSyncAt
        ? formatDistanceToNow(new Date(stats.value.lastSyncAt), { addSuffix: false })
        : ''
      return `${stats.value.count} syncing • last failed ${ago}`
    }
    const ago = stats.value.lastSyncAt
      ? formatDistanceToNow(new Date(stats.value.lastSyncAt), { addSuffix: false })
      : ''
    return `Last sync failed ${ago}`
  }
  if (stats.value.count > 0) return `${stats.value.count} syncing`
  return 'All synced'
})

const showLink = computed(() => stats.value.count > 0 || stats.value.hasFailures)
</script>

<template>
  <router-link
    :to="`/app/tasks?entityType=${props.entityType}`"
    class="entity-task-badge"
  >
    <Badge :variant="variant" size="sm">
      <component :is="icon" class="h-3 w-3" />
      {{ label }}
    </Badge>
    <a v-if="showLink" class="badge-link">View sync history</a>
  </router-link>
</template>

<style scoped>
.entity-task-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  text-decoration: none;
  color: inherit;
}
.badge-link {
  font-size: 0.7rem;
  color: var(--color-primary);
  text-decoration: none;
}
.badge-link:hover {
  text-decoration: underline;
}
</style>
